import { ApplicationProcess } from "./Application";
import { Issue, ScreeningStatusId } from "./Screening";

export interface ExclusionListFurtherReviewData {
  score?: number;
  risk?: RiskLevelEnum;
  percentage?: string;
  color?: RiskColorEnum;
  isHighRisk?: boolean;
  isMediumRisk?: boolean;
}

// Lease Domain-only (mirrors LeaseDomain.API's PublicApplicantControls) - legacy applicants have
// no equivalent concept, so this is undefined/absent for legacy applications, never populated.
export interface ApplicantControls {
  canBeRemoved?: boolean;
  canBeReminded?: boolean;
  canModifyApplication?: boolean;
  canCancelApplication?: boolean;
  canModifyAddons?: boolean;
  canDocumentScreening?: boolean;
  canIDVerification?: boolean;
  canCreditVerification?: boolean;
  canModifyApplicant?: boolean;
  canAddDependents?: boolean;
  canAddApplicants?: boolean;
  canAddGuarantors?: boolean;
  canPayApplicationFees?: boolean;
  canPaySecurityDeposit?: boolean;
  canSubmit?: boolean;
  canRemoveOthers?: boolean;
  canRemindOthers?: boolean;
  canCompleteAddendums?: boolean;
  canGenerateLease?: boolean;
  isApplicationUnprocessable?: boolean;
  canExtendHold?: boolean;
  requiresAssociateResolution?: boolean;
  removeRestriction?: string | null;
}

export interface Applicant {
  id: number;
  controls?: ApplicantControls;
  authenticationId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  suffix: string;
  emailAddress: string;
  phoneNumber: string;
  phoneNumberType: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  stateLicenseIssuer: string;
  stateLicenseNumber: string;
  ssn: string;
  gender: string;
  householdStatus: string;
  notEmployed: boolean;
  annualIncome: number;
  nonUSPhone: boolean;
  isSSN: boolean;
  ssnValidForWorkOnly: boolean;
  isMiddleName: boolean;
  applicantTypeId: ApplicantType;
  // number for legacy (a numeric user id); string for Lease Domain (lease_applicants.created_by/
  // updated_by, an associate email/system identifier) - see AP2-6392 second-tier fields.
  createdBy: number | string;
  createdDate: string;
  smsPreference: boolean;
  emailPreference: boolean;
  modifiedBy: number | string;
  modifiedDate: string;
  applicantEmployer: ApplicantEmployer[];
  applicationProcess: ApplicationProcess;
  applicantInvite: any | null;
  applicantScreening: ApplicantScreening[];
  isSoftDelete?: boolean;
  isOnExclusionList?: boolean;
  riskScore?: number;
  exclusionReason?: string;
  riskAssessmentCancellationTemplate?: string;
  exclusionListFurtherReviewClearedBy?: string;
  exclusionListFurtherReviewClearedDate?: string;
  exclusionListFurtherReviewData: ExclusionListFurtherReviewData;
  // AP2-6506: Lease Domain-sourced, sees the applicant-details popup's payment-status/terms
  // sections without a separate legacy-only proxy call - see setApplicantPaymentAndTermsFromLeaseDomain.
  applicationFeePaymentStatus?: string | null;
  tcAcknowledgementDate?: string | null;
}

export enum ScreeningVendorId {
  ONFIDO = 1,
  FADV,
  SNAPPT,
  TRANSUNION,
}

export enum ScreeningVendor {
  ONFIDO = "Onfido",
  FADV = "FADV",
  SNAPPT = "Snappt",
  TRANSUNION = "TransUnion",
}

export enum ScreeningType {
  BACKGROUND = "Background",
  CREDIT = "Credit",
  IDENTITY = "Identity",
  SNAPPT = "Snappt",
}

export interface ApplicantScreening {
  id: number;
  tenantId: number;
  applicantId: number;
  screenVendorApplicantId: string;
  screeningVendor: ScreeningVendor;
  screeningVendorId: ScreeningVendorId;
  screeningType: string;
  screeningTypeId: number;
  screeningStatus: string;
  screeningStatusDetails?: string | null;
  groupScreeningStatus?: string | null;
  groupScreeningStatusDetails?: string | null;
  screeningStatusId: ScreeningStatusId;
  isSoftDeleted: boolean;
  noOfAttempts: number;
  createdOn: string;
  // number for legacy; string for Lease Domain - see Applicant.createdBy above.
  createdBy: number | string;
  modifiedDate: string;
  modifiedBy: number | string;
  issues: Issue[];
}

export interface ApplicantEmployer {
  id?: number;
  employer: string;
  industry: string;
  employerPhone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

export enum ApplicantType {
  Applicant = 1,
  CoTenant,
  Guarantor,
  Dependent,
}

export enum RiskLevelEnum {
  HIGH = "High Risk",
  MEDIUM = "Medium Risk",
  LOW = "Low Risk",
}

export enum RiskColorEnum {
  HIGHRISK = "#F5222D",
  MEDIUMRISK = "#FF9800",
}

export const CancelledDueToHighRisk = -4;

// Lease Domain's real cancel_reason_cd lookup table (application_cancel_reason), mirrored here
// as the enum legacy never had - legacy instead overloads cancelledBy with the CancelledDueToHighRisk
// sentinel above for the one reason it needs to distinguish. ApplicantCancellation and
// ApplicantHighRisk are defined in Lease Domain's lookup table but not currently written by any
// Lease Domain write path as of AP2-6392 - kept here because the lookup table is the source of
// truth for the full set of values the field can hold, not just the ones observed live so far.
export enum LeaseDomainCancelReason {
  ApplicantRequest = "ApplicantRequest",
  ApplicantCancellation = "ApplicantCancellation",
  AssociateCancellation = "AssociateCancellation",
  ApplicantExclusion = "ApplicantExclusion",
  ApplicantHighRisk = "ApplicantHighRisk",
  Expiration = "Expiration",
  SystemCancellation = "SystemCancellation",
}
